import { useEffect, useMemo, useRef, useState } from 'react';

import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useBooleanState } from '../../../../hooks/useBooleanState';
import { useMainDashboardFilter } from '../../../../hooks/useMainDashboardFilter';
import { Props } from '../../QualificationWidget/types';
import { LocationWithGeo, ProximityToWorkData } from '../../../../types';
import { getLocationsWithGeoRequest } from '../../../../api/time';
import { getProximityToWorkRequest } from '../../../../api/teams';
import { fetchTagsRequest, getBoroughsRequest } from '../../../../api/handbook';
import { getUrlItems } from '../../../../utils/url';
import { Borough, Tag, TagGroups } from '../../../../types';

export type FilterMode = 'distance' | 'borough';

export const useProximityToWorkWidgetData = ({ selectedCompanyId, mainDashboardFilters }: Props) => {
  const methods = useForm<{
    filterMode: FilterMode;
    siteId: string;
    teamId: string;
    boroughs: Borough[];
    jobTitles: string[];
  }>({
    mode: 'all',
    reValidateMode: 'onChange',
    defaultValues: {
      filterMode: 'distance',
      siteId: '',
      teamId: 'all',
      boroughs: [],
      jobTitles: [],
    },
  });
  const [locationsWithGeo, setLocationsWithGeo] = useState<LocationWithGeo[]>([]);
  const [jobTitleOptions, setJobTitleOptions] = useState<string[]>([]);
  const [boroughs, setBoroughs] = useState<Borough[]>([]);
  const [proximityToWorkData, setProximityToWorkData] = useState<ProximityToWorkData | null>(null);
  const [loading, setLoading] = useState(false);
  const [isOpenFilterDialog, setFilterDialogOpen, setFilterDialogFalse] = useBooleanState(false);
  const { team } = useParams<{ team: string }>();
  const teamId = team ? getUrlItems(team)?.id : undefined;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const values = methods.watch();
  const selectedSite = useMemo(
    () => locationsWithGeo.find(location => location.uuid === values.siteId),
    [locationsWithGeo, values.siteId]
  );

  const { selectedTeamOptions, unselectedTeamOptions, teams } = useMainDashboardFilter({
    mainDashboardFilters,
  });

  const getLocations = async () => {
    try {
      const res = await getLocationsWithGeoRequest(selectedCompanyId);
      if (!isMountedRef.current) return;
      setLocationsWithGeo(
        res.data.sort((a: LocationWithGeo, b: LocationWithGeo) => a.name.localeCompare(b.name))
      );
    } catch (error) {
      console.error(error);
    }
  };

  const getProximityToWork = async (
    teamId: string | null,
    siteId: string,
    boroughIds: string[],
    filterMode: FilterMode,
    jobTitles: string[]
  ) => {
    try {
      setLoading(true);
      const isBoroughMode = filterMode === 'borough';
      const payload: Parameters<typeof getProximityToWorkRequest>[1] = {
        team_ids: teamId ? [teamId] : null,
        distance_ranges: [1, 2, 3, 4],
        time_distance: 45,
        job_titles: jobTitles.length ? jobTitles : undefined,
      };
      if (isBoroughMode) {
        payload.borough_ids = boroughIds.length > 0 ? boroughIds : undefined;
      } else {
        payload.geo_polygon = locationsWithGeo.find(location => location.uuid === siteId)?.geo_polygon ?? '';
      }
      const res = await getProximityToWorkRequest(selectedCompanyId, payload);
      if (!isMountedRef.current) return;
      setProximityToWorkData(res.data as any);
      setLoading(false);
    } catch (error) {
      if (!isMountedRef.current) return;
      setProximityToWorkData(null);
      setLoading(false);
      console.error(error);
    }
  };

  const getBoroughs = async () => {
    try {
      const res = await getBoroughsRequest();
      const items = res.data?.items ?? res.data ?? [];
      const list = Array.isArray(items)
        ? items.map((item: Borough) => ({
            uuid: item.uuid ?? '',
            name: item.name,
            boundary: item.boundary,
          }))
        : [];
      if (!isMountedRef.current) return;
      setBoroughs(list.filter(b => b.uuid).sort((a, b) => a.name.localeCompare(b.name)));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getBoroughs();
  }, []);

  const getJobTitles = async () => {
    try {
      const res = await fetchTagsRequest({ group: TagGroups.ROLE, short: false });
      if (!isMountedRef.current) return;
      setJobTitleOptions(res.data.items.map((t: Tag) => t.name));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!selectedCompanyId) {
      return;
    }
    getJobTitles();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId || !values.teamId) {
      return;
    }
    const teamIds = values.teamId === 'all' ? null : values.teamId;
    const jobTitles = values.jobTitles ?? [];
    const validBoroughIds =
      values.filterMode === 'borough'
        ? (values.boroughs ?? []).map(b => b.uuid).filter(id => id && id !== '-1')
        : [];
    if (values.filterMode === 'borough') {
      if (validBoroughIds.length === 0) {
        return;
      }
      getProximityToWork(teamIds, '', validBoroughIds, 'borough', jobTitles);
    } else {
      if (!values.siteId) {
        return;
      }
      getProximityToWork(teamIds, values.siteId, [], 'distance', jobTitles);
    }
  }, [values.teamId, values.siteId, values.boroughs, values.filterMode, values.jobTitles]);

  useEffect(() => {
    getLocations();
  }, [selectedCompanyId]);

  useEffect(() => {
    if (!selectedCompanyId || !teams.length) {
      return;
    }
    // Use teamId from URL if available, otherwise default to All teams
    const teamIdToSet = teamId ?? 'all';
    methods.setValue('teamId', teamIdToSet);
  }, [teams, teamId]);

  useEffect(() => {
    if (!selectedCompanyId || !locationsWithGeo.length) {
      return;
    }
    const firstLocationWithGeo = locationsWithGeo.find(loc => loc.geo_point || loc.geo_polygon);
    if (firstLocationWithGeo) {
      methods.setValue('siteId', firstLocationWithGeo.uuid);
    }
  }, [locationsWithGeo]);

  const teamOptionsWithAll = useMemo(() => {
    const allTeamsOption = { value: 'all', name: 'All teams' };
    const filteredSelected = selectedTeamOptions.filter(
      option => option.name !== 'All Teams' && option.name !== 'Selected Teams'
    );
    const filteredUnselected = unselectedTeamOptions.filter(option => option.name !== 'All Teams');
    return {
      groupOneOptions: [allTeamsOption, ...filteredSelected],
      groupTwoOptions: filteredUnselected,
    };
  }, [selectedTeamOptions, unselectedTeamOptions]);

  const selectedTeamName = useMemo(() => {
    const allTeams = [...teamOptionsWithAll.groupOneOptions, ...teamOptionsWithAll.groupTwoOptions];
    const team = allTeams.find(t => t.value === values.teamId);
    return team?.name ?? '';
  }, [values.teamId, teamOptionsWithAll]);

  const selectedLocationName = useMemo(() => {
    const location = locationsWithGeo.find(l => l.uuid === values.siteId);
    return location?.name ?? '';
  }, [values.siteId, locationsWithGeo]);

  const selectedBoroughNames = useMemo(() => {
    return (values.boroughs ?? [])
      .filter(b => b.uuid && b.uuid !== '-1')
      .map(b => b.name)
      .filter(Boolean)
      .join(', ');
  }, [values.boroughs]);

  const selectedBoroughsWithBoundary = useMemo(() => {
    return (values.boroughs ?? []).filter(
      (b): b is { uuid: string; name: string; boundary: string } =>
        !!b.boundary && !!b.uuid && b.uuid !== '-1'
    );
  }, [values.boroughs]);

  return {
    methods,
    filterMode: values.filterMode,
    ...teamOptionsWithAll,
    locationsWithGeo,
    selectedSite,
    proximityToWorkData,
    loading,
    teamId,
    isOpenFilterDialog,
    setFilterDialogOpen,
    setFilterDialogFalse,
    selectedTeamName,
    selectedLocationName,
    boroughs,
    selectedBoroughNames,
    selectedBoroughsWithBoundary,
    jobTitleOptions,
  };
};
